import type { Cable, System, SystemNode } from "../types";

export type AssistantItemChange<T> =
  | { kind: "added"; after: T; before?: never }
  | { kind: "changed"; before: T; after: T }
  | { kind: "removed"; before: T; after?: never };

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, i) => sameValue(item, b[i]));
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left).filter(key => left[key] !== undefined);
  return keys.length === Object.keys(right).filter(key => right[key] !== undefined).length &&
    keys.every(key => sameValue(left[key], right[key]));
}

function changes<T extends { id: string }>(before: T[], after: T[]): AssistantItemChange<T>[] {
  const previous = new Map(before.map(item => [item.id, item]));
  const result: AssistantItemChange<T>[] = [];
  for (const item of after) {
    const old = previous.get(item.id);
    if (!old) result.push({ kind: "added", after: item });
    else if (!sameValue(old, item)) result.push({ kind: "changed", before: old, after: item });
    previous.delete(item.id);
  }
  for (const item of previous.values()) result.push({ kind: "removed", before: item });
  return result;
}

/** Compare snapshots by identity, including edits which leave the item count unchanged. */
export function assistantEditDiff(before: System, after: System): {
  nodes: AssistantItemChange<SystemNode>[];
  cables: AssistantItemChange<Cable>[];
} {
  return { nodes: changes(before.nodes, after.nodes), cables: changes(before.cables, after.cables) };
}
